import { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { withTranslation } from "react-i18next";
import debug from "@plannel/utils/debug-util";
import { isEqual, values, keys, uniq, size, isEmpty, isNil, includes } from "lodash";

import { Box, Button, Popover, Typography, Menu, MenuItem, TextField, InputAdornment, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import StatPill from "@plannel/components/ui/StatPill";
import { MeasureActiveCell, MeasureListCell } from "@plannel/components/aggrid/MeasureGridCells";
import MeasureDetailPanel from "@plannel/pages/demand-plan/dp-config/MeasureDetailPanel";
import "@plannel/scss/components/MeasureSheetGrid.scss";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import codeUtil from "@plannel/pages/utils/code-util";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ColorUtil from "@plannel/pages/utils/color-util";

import measureService from "@plannel/services/data/measure-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import WorkbenchUtil from "../WorkbenchUtil";
import { FS } from "@plannel/utils/font-util";

const CustomBooleanIcon = activeProps => {
  const { displayName, selectAll: select, setSelectAll: setSelect } = activeProps;

  const allCheckActive = e => {
    const allActive = e?.target?.checked;
    setSelect(allActive);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box>
        <input
          type="checkbox"
          defaultChecked={select}
          onClick={e => allCheckActive(e)}
        />
      </Box>
      <Box className="customHeaderLabel">{displayName}</Box>
    </Box>
  );
};

// 시트 툴바 버튼 — 검색창(30px 알약)과 같은 높이·모서리
const TOOLBAR_BUTTON_SX = {
  height: 30,
  px: 1.5,
  borderRadius: 999,
  fontSize: FS.SUB,
  textTransform: "none",
  whiteSpace: "nowrap",
};

const ALL_MEASURES = "ALL";
const ACTIVE_ONLY = "ACTIVE";
/*
 * 채널 전용 측정치(MeasureType.java 의 channelOnlyMeasure — DpSettings.js 의 CHANNEL_ONLY_MEASURE_CDS
 * 와 동기화 유지) — 일반 Workbench(isChannel=false)의 measure 관리 목록에는 안 보여야 한다.
 * POS_QTY 등은 z_measure 에 행이 있어 measureService.getAll 에도 함께 잡히므로 여기서 걸러낸다.
 */
const CHANNEL_ONLY_MEASURE_CDS = codeUtil.channelOnlyMeasureCds;

const DPMeasureModal = props => {
  const { t, close, open, data, modalResultData, filter: { version }, isChannel } = props;
  const gridRef = useRef();
  const { module, dpMeasureType, dpAggRules, dpDisAggRules, dpDisAggTarget, dpVersion, 
    dpMeasure: { BF_QTY, LR_QTY, LR_AMT, FF_QTY, FF_AMT, SALES_PLAN_QTY, SALES_PLAN_AMT, ASA_QTY, ASA_AMT }, dpConfig, USER_MEASURE } = codeUtil; // nonRuleMeasureCds,
  const { LR, DISAGGREGATION_TARGET, AVG_FLG } = dpConfig;
  const valueType = useRef(values(dpMeasureType));

  const [measureModal, setMeasureModal] = useState([]);
  const [measureType, setMeasureType] = useState([]);

  const aggRules = values(dpAggRules);
  const disaggRules = values(dpDisAggRules);
  const disaggTarget = useRef(keys(dpDisAggTarget)[0]);
  const disaggTargetForSalesPlan = useRef(keys(dpDisAggTarget)[0]);
  const avgMonths = useRef(3);

  const [oriMeasureList, setOriMeasureList] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [popoverEl, setPopoverEl] = useState(null);
  const [menuEl, setMenuEl] = useState(null);
  const menuOpen = Boolean(menuEl);

  const [selectAll, setSelectAll] = useState(false);
  // 헤더 체크박스를 실제로 누른 경우에만 전체 켜기/끄기를 돌린다(아래 useEffect 참고).
  const selectAllTouched = useRef(false);
  const handleSelectAll = value => {
    selectAllTouched.current = true;
    setSelectAll(value);
  };
  // 오른쪽 편집 패널 — 목록에서 고른 행 하나(행 데이터는 제자리에서 바뀌므로 사본으로 그린다)
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedRowSnapshot, setSelectedRowSnapshot] = useState(null);
  const selectedNodeRef = useRef(null);
  // 수식 칸에 끼워 넣을 조각 — "[" 로 연 measure 메뉴에서 고른 값
  const [formulaInsert, setFormulaInsert] = useState(null);
  const formulaInsertSeq = useRef(0);
  // 검색어 / Active only / 개수
  const [measureKeyword, setMeasureKeyword] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [measureCounts, setMeasureCounts] = useState({ shown: 0, total: 0, active: 0 });
  const measureFilterRef = useRef({ keyword: "", activeOnly: false });
  const selectedVersion = useRef({});
  const versions = useRef([]);
  const MAX_SIZE = 10000;
  const { inputError, validation, saveFail, noSaveData, saveSuccess } = ColorUtil.snackBarMessageColor;

  const menuClose = () => {
    setMenuEl(null);
  };

  const popoverClose = () => {
    setPopoverEl(null);
  };

  const popoverOpen = Boolean(popoverEl);
  const id = popoverOpen ? "simple-popover" : null;

  const getRuleName = (cd, isAgg) => {
    if (isAgg) {
      return dpAggRules[cd];
    }
    if (cd === "RATIO") {
      return dpDisAggRules.ITEM_RATIO;
    }
    return dpDisAggRules[cd];
  };

  const sortListByColumn = (list, columnName) => {
    list.sort((a, b) => {
      if (a[columnName] > b[columnName]) {
        return 1;
      }
      if (a[columnName] < b[columnName]) {
        return -1;
      }
      return 0;
    });
  
    return list;
  };

  const setGridDatas = modalData => {
    setOriMeasureList(modalData);
    const datas = [];
    const types = [];
    for (const measure of modalData) {
      let selectVer = [];
      const measureCode = measure.measureCd;
      const isLr = measureCode === LR_AMT.cd || measureCode === LR_QTY.cd;
      const isFf = measureCode === FF_AMT.cd || measureCode === FF_QTY.cd;
      const isSalesPlan =measureCode === SALES_PLAN_AMT.cd || measureCode === SALES_PLAN_QTY.cd;
      const isAsa = measureCode === ASA_AMT.cd || measureCode === ASA_QTY.cd;

      if (isLr) {
        if (selectedVersion.current && versions.current) {
          const versionId = selectedVersion.current?.objVal?.LR_VER_ID;
          selectVer = versions.current.filter(el => el.id === versionId);
        }
      }
      const row = {
        id: measure.id,
        seqNum: measure.seqNum,
        computeFlg: measure.computeFlg,
        measureCd: measureCode,
        name: measure.name,
        valueTypeCd: measure.valueTypeCd,
        formulaExpValue: measure.formulaExp ? measure.formulaExp : null,
        formulaExp: measure.formulaExp ? WorkbenchUtil.changeExpression(measure.formulaExp, modalData, true) : null,
        aggRuleCd: getRuleName(measure.aggRuleCd, true),
        disaggRuleCd: getRuleName(measure.disaggRuleCd, false),
        disaggUnitFlg: measure.disaggUnitFlg,
        activeFlg: measure.activeFlg,
        simFlg: measure.simFlg,
        owner: measure.owner,
        descTxt: measure.descTxt,
        option: selectVer?.[0],
        optionText: isLr ? `${t("releaseVersion")}:${t("MSG_ReleaseVersion")}` : "",
        verNum: measure.verNum,
        isUserMeasure: measure?.measureCd.includes("USER_MEASURE") ? true : false,
        measureEditable: measure?.measureEditable
      };

      if (isFf) {
        row.option = !isEmpty(disaggTarget.current) ? disaggTarget.current : dpDisAggTarget.QTY;
        row.optionText = `${t("disaggregationTarget")}:${t("MSG_DisaggregationTarget")}`;
      }
      if (isSalesPlan) {
        row.option = !isEmpty(disaggTargetForSalesPlan.current) ? disaggTargetForSalesPlan.current : dpDisAggTarget.QTY;
        row.optionText = `${t("disaggregationTarget")}:${t("MSG_DisaggregationTarget")}`;
      }
      if (isAsa) {
        row.option = avgMonths.current + t("months");
      }

      const disaggegationTarget = isFf ? disaggTarget.current : isSalesPlan ? disaggTargetForSalesPlan.current : null;
      const disaggQtyMeasure = isFf ? FF_QTY.cd : isSalesPlan ? SALES_PLAN_QTY.cd : null;
      const disaggAmtMeasure = isFf ? FF_AMT.cd : isSalesPlan ? SALES_PLAN_AMT.cd : null;

      // system measure && qty, amt 모두 false이면 amt를 true로 세팅
      const isEditableSysMeasure = measure.measureEditable && !row.simFlg && !row.computeFlg;
      if (disaggegationTarget === null && isEditableSysMeasure && row.valueTypeCd === dpMeasureType.AMT) {
        const measureCd = row.measureCd;
        const pairMeasureCd = measureCd.replace(row.valueTypeCd, dpMeasureType.QTY);
        const pairMeasure = modalData.filter(el => el.measureCd === pairMeasureCd);
        if (pairMeasure !== null && !row.disaggUnitFlg && !pairMeasure[0]?.disaggUnitFlg) {
          row.disaggUnitFlg = true;
        }
      }
      // independent measure can edit both qty and amt
      if (disaggegationTarget === dpDisAggTarget.QTY.cd) {
        if (row.measureCd === disaggQtyMeasure) {
          row.disaggUnitFlg = true;
        } else if (row.measureCd === disaggAmtMeasure) {
          row.disaggUnitFlg = false;
        }
      } else if (disaggegationTarget === dpDisAggTarget.AMT.cd) {
        if (row.measureCd === disaggQtyMeasure) {
          row.disaggUnitFlg = false;
        } else if (row.measureCd === disaggAmtMeasure) {
          row.disaggUnitFlg = true;
        }
      } else {
        if (row.measureCd === disaggQtyMeasure) {
          row.disaggUnitFlg = true;
        } else if (row.measureCd === disaggAmtMeasure) {
          row.disaggUnitFlg = true;
        }
      }
      //user measure && not compute measure
      if (row.isUserMeasure) {
        //user measure is editable
        row.disaggUnitFlg = false;
      }

      if (!row.activeFlg) {
        row.seqNum = MAX_SIZE;
      }

      types.push(row.valueTypeCd);
      types.push(...valueType.current);
      datas.push(row);
    }
    let result = sortListByColumn(datas, "name");
    result = sortListByColumn(result, "isUserMeasure");
    result = sortListByColumn(result, "seqNum");
    setMeasureType(uniq(types));
    setMeasureModal(result);
  };

  const activeFlgNSeqNum = param => {
    if (param.value === MAX_SIZE) {
      return "";
    }
    return param.value;
  };

  const statusValueCellRenderer = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  };

  /*
   * 수식 칸에서 "[" 로 고른 measure 를 커서 자리에 끼워 넣는다.
   * 편집 중인 값은 오른쪽 패널 입력칸이 들고 있으므로, 넣을 조각만 내려보낸다.
   */
  const addExpression = measureName => {
    formulaInsertSeq.current += 1;
    setFormulaInsert({ text: `${GridUtils.gridValueL10N(measureName)}]`, seq: formulaInsertSeq.current });
    menuClose();
  };

  /*
   * 고칠 수 있는 measure 인지.
   * - simFlg : 이 모달에서 직접 추가한 시뮬레이션 measure
   * - isUserMeasure : DP Settings 에서 만든 사용자 measure (USER_MEASURE 코드)
   * 시스템 기본 measure 만 잠근다 — DP Settings 의 편집 범위와 맞춘다.
   */
  const isEditableMeasure = data => !!data?.simFlg || !!data?.isUserMeasure;

  const setEditable = params => {
    return isEditableMeasure(params.data);
  };

  // 목록 한 줄 요약 — 이름·계산식여부·타입·사용자여부·(집계 · 배분 · 옵션)
  const measureSummary = data => {
    const agg = GridUtils.gridValueL10N(data?.aggRuleCd?.name);
    const disagg = GridUtils.gridValueL10N(data?.disaggRuleCd?.name);
    const option = optionFormatter({ data, value: data?.option });

    return {
      // 기본값 그대로일 때만 번역 — 사용자가 지은 이름은 그대로(GridUtils.measureLabel 주석)
      name: GridUtils.measureLabel(data?.name, codeUtil.dpMeasure[data?.measureCd]?.name),
      fx: !!data?.computeFlg,
      type: GridUtils.gridValueL10N(data?.valueTypeCd),
      // 사용자가 만든 measure 표시 — DP Settings 와 같은 기준(measureCd 에 USER_MEASURE)
      user: !!data?.isUserMeasure,
      original: null,
      meta: [agg, disagg, option].filter(Boolean).join(" · "),
    };
  };

  /*
   * 개수는 그리드에서 센다 — 행 추가(applyTransaction)는 rowData state 를 거치지 않는다.
   */
  const refreshMeasureCounts = api => {
    if (!api || api.isDestroyed?.()) return;

    let total = 0;
    let active = 0;
    api.forEachNode(node => {
      total += 1;
      if (node?.data?.activeFlg) {
        active += 1;
      }
    });

    setMeasureCounts({ shown: api.getDisplayedRowCount(), total, active });
  };

  const pickMeasureNode = node => {
    selectedNodeRef.current = node ?? null;
    setSelectedNodeId(node?.id ?? null);
    setSelectedRowSnapshot(node?.data ? { ...node.data } : null);
  };

  /*
   * 왼쪽 목록에서 바꾼 값을 오른쪽 패널에도 비친다.
   * 행 클릭 때 뜬 사본으로는 부족하다 — AG Grid 는 colDef.onCellClicked(=ON 스위치 토글)를
   * setTimeout(0) 으로 미뤄 부르므로 onRowClicked(사본 생성)보다 늦게 돈다.
   * 그래서 값이 실제로 바뀌는 이 시점에 사본을 다시 뜬다(전체 켜기/끄기도 같은 경로).
   */
  const onCellValueChanged = params => {
    // 붙여넣기·변경행 추적은 기본 설정의 핸들러가 하던 일이므로 그대로 태운다.
    DefaultGridSetting({}).onCellValueChanged(params);

    if (params.node && params.node === selectedNodeRef.current) {
      setSelectedRowSnapshot({ ...params.node.data });
    }
    refreshMeasureCounts(params.api);
  };

  // 검색어(이름/코드) + Active only 를 external filter 로 건다.
  const isMeasureFilterPresent = () => {
    const { keyword, activeOnly: onlyActive } = measureFilterRef.current;
    return onlyActive || keyword.trim() !== "";
  };

  const doesMeasureFilterPass = node => {
    const { keyword, activeOnly: onlyActive } = measureFilterRef.current;
    const data = node?.data ?? {};

    if (onlyActive && !data.activeFlg) {
      return false;
    }

    const word = keyword.trim().toLowerCase();
    if (!word) {
      return true;
    }

    return String(GridUtils.measureLabel(data.name, codeUtil.dpMeasure[data?.measureCd]?.name) ?? "").toLowerCase().includes(word)
      || String(data.name ?? "").toLowerCase().includes(word)
      || String(data.measureCd ?? "").toLowerCase().includes(word);
  };

  const applyMeasureFilter = next => {
    measureFilterRef.current = { ...measureFilterRef.current, ...next };
    gridApi?.onFilterChanged();
  };

  /*
   * 전체 켜기/끄기 — 사용자가 헤더 체크박스를 누른 뒤에만 돈다.
   * 마운트/불러오기 직후에도 돌면(초기값 false) 모든 measure 가 한꺼번에 꺼진다.
   */
  useEffect(() => {
    if (!selectAllTouched.current) return;
    if (gridApi) {
      gridApi.forEachNode(node => {
        const rowNodeData = node.data;
        if (rowNodeData?.activeFlg !== selectAll) {
          rowNodeData.activeFlg = selectAll;
          node.setDataValue("activeFlg", selectAll);
          const updateRow = gridApi.applyTransaction({ update: [rowNodeData] }).update[0];
          DataState.setDataState(updateRow);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  const onClikedDisaggUnit = params => {
    const disableMeasure = [BF_QTY.cd, FF_QTY.cd, FF_AMT.cd, SALES_PLAN_QTY.cd, SALES_PLAN_AMT.cd];
    const isEditableSysMeasure = params.data.measureEditable && !params.data.simFlg && !params.data.computeFlg;
    if (isEditableSysMeasure && !disableMeasure.includes(params.data?.measureCd)) {
      params.node.setDataValue(params.column, !params.value);
      const nodeData = params.node.data;
      if (gridApi) {
        gridApi.forEachNode(node => {
          if (node.data?.measureCd && node.data?.valueTypeCd && nodeData?.measureCd && nodeData?.valueTypeCd) {
            const compareMeasure = node.data.measureCd.replace(node.data.valueTypeCd, "");
            const changeMeasure = nodeData?.measureCd.replace(nodeData?.valueTypeCd, "");
            if (isEqual(compareMeasure, changeMeasure) && !isEqual(node.data?.measureCd, nodeData?.measureCd)) {
              node.setDataValue("disaggUnitFlg", params.value);
            }
          }
        });
      }
    }
  };


  // 릴리즈된 버전에는 measure 를 새로 넣을 수 없다(추가 버튼도 함께 잠근다).
  const isReleasedVersion = isEqual(version?.statusCd, dpVersion.RELEASED);

  const addRow = () => {
    if (isReleasedVersion) {
      setSnackInfo({ open: true, severity: inputError, content: t("MSG_ReleaseVersionMeasureAddNotAvail") });
      return false;
    }
    const newRow = {
      id: null,
      seqNum: null,
      measureCd: null,
      computeFlg: false,
      name: null,
      valueTypeCd: dpMeasureType.AMT,
      formulaExpValue: null,
      formulaExp: null,
      aggRuleCd: dpAggRules.SUM,
      disaggRuleCd: dpDisAggRules.PROPORTION,
      disaggUnitFlg: false,
      activeFlg: true,
      owner: null,
      descTxt: null,
      simFlg: true,
    };

    // 선택 상태로 만들지 않는다 — 체크박스 열이 다시 나타난다.
    const createdNode = GridUtils.addRow(gridApi, newRow, measureModal.length + 1, false);
    DataState.setDataState(createdNode);
    pickMeasureNode(createdNode);
    refreshMeasureCounts(gridApi);
    return true;
  };

  const onRowDragEnd = useCallback(() => {
    let startNum = 1;
    if (gridApi) {
      gridApi.forEachNode(rowNode => {
        const rowNodeData = rowNode.data;
        if (rowNodeData.activeFlg) {
          rowNodeData.seqNum = startNum;
          startNum++;
          const updateRow = gridApi.applyTransaction({ update: [rowNodeData] }).update[0];
          DataState.setDataState(updateRow);
        } else {
          rowNodeData.seqNum = MAX_SIZE;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const optionFormatter = (params) => {
    const hasOptionMeasureCds = [FF_QTY.cd, FF_AMT.cd, SALES_PLAN_QTY.cd, SALES_PLAN_AMT.cd];
    if (hasOptionMeasureCds.includes(params?.data?.measureCd)) {
      if (params.value) {
        return GridUtils.gridValueL10N(params.value?.name ?? params.value?.toString().toLowerCase());
      }
    }
    if (params?.data?.measureCd === LR_AMT.cd || params?.data?.measureCd === LR_QTY.cd) {
      return params.value?.name;
    }
    if (params?.data?.measureCd === ASA_AMT.cd || params?.data?.measureCd === ASA_QTY.cd) {
      return params.value;
    }
    return "";
  };

  const measureColumnDefs = [
    {
      headerName: "",
      field: "id",
      editable: false,
      type: ["defaultValueFormatter"],
      hide: true,
      suppressColumnsToolPanel: true
    },
    {
      // 순서 바꾸기 손잡이 — Workbench 에 보이는 measure 순서가 여기서 정해진다.
      colId: "dragHandle",
      headerName: "",
      field: "",
      width: 34,
      editable: false,
      sortable: false,
      rowDrag: true,
      suppressHeaderMenuButton: true,
      menuTabs: [],
      pinned: "left", lockPinned: true,
      suppressColumnsToolPanel: true
    },
    {
      headerName: "seq",
      field: "seqNum",
      width: 100,
      editable: false,
      hide: true,
      valueFormatter: activeFlgNSeqNum,
    },
    {
      headerName: "displayName",
      field: "measureCd",
      width: 85,
      hide: true,
      editable: setEditable,
    },
    {
      headerName: "activeFlg",
      field: "activeFlg",
      width: 76,
      editable: true,
      type: ["booleanColumn"],
      cellRenderer: MeasureActiveCell,
      pinned: "left", lockPinned: true,
      headerComponent: CustomBooleanIcon,
      headerComponentParams: {
        enableMenu: false,
        setSelectAll: handleSelectAll,
        selectAll,
      },
    },
    {
      headerName: "simFlg",
      field: "simFlg",
      hide: true,
      suppressColumnsToolPanel: true
    },
    {
      headerName: "measure",
      field: "name",
      hide: true,
      editable: setEditable,
      valueFormatter: statusValueCellRenderer,
    },
    /*
     * 목록에 보이는 유일한 값 컬럼 — 이름·ƒ·타입·설정요약을 한 줄로.
     * 아래 속성 컬럼들은 값 저장(setDataValue)과 기존 저장 경로를 위해 숨긴 채로 남긴다.
     */
    {
      colId: "measureSummary",
      headerName: "measure",
      flex: 1,
      minWidth: 240,
      editable: false,
      sortable: false,
      cellRenderer: MeasureListCell,
      cellRendererParams: { summary: data => measureSummary(data), userLabel: t("user") },
    },
    {
      headerName: "compute",
      field: "computeFlg",
      width: 100,
      hide: true,
      editable: params => isEditableMeasure(params.data),
    },
    {
      headerName: "type",
      field: "valueTypeCd",
      width: 80,
      hide: true,
      editable: setEditable,
      valueFormatter: statusValueCellRenderer,
      type: ["defaultValueParser"],
    },
    {
      headerName: "aggregation",
      field: "aggRuleCd",
      width: 140,
      hide: true,
      editable: setEditable,
      valueFormatter: rule => {
        return GridUtils.gridValueL10N(rule.value?.name);
      },
      type: ["defaultValueParser"],
    },
    {
      headerName: "rule",
      field: "disaggRuleCd",
      width: 140,
      hide: true,
      editable: setEditable,
      valueFormatter: rule => {
        return GridUtils.gridValueL10N(rule.value?.name);
      },
      type: ["defaultValueParser"],
    },
    {
      headerName: "editTarget",
      field: "disaggUnitFlg",
      width: 100,
      hide: true,
      editable: false,
    },
    {
      headerName: "option",
      field: "option", width: 200,
      hide: true,
      tooltipField: "optionText",
      editable: false,
      valueFormatter: params => { return optionFormatter(params)},
    },
    {
      headerName: "formula",
      field: "formulaExp",
      width: 300,
      hide: true,
      editable: params => params.data.computeFlg && isEditableMeasure(params.data),
      menuTabs: ["generalMenuTab"],
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        isQuestion: true,
        enableMenu: false,
        onClickedEvent: setPopoverEl,
      },
    },
    {
      headerName: "formula",
      field: "formulaExpValue",
      hide: true,
      suppressColumnsToolPanel: true
    },
    {
      headerName: "descTxt",
      field: "descTxt",
      hide: true,
      editable: setEditable,
      minWidth: 50,
    },
    {
      headerName: "verNum",
      field: "verNum",
      hide: true,
      suppressColumnsToolPanel: true
    },
  ];

  // 노드는 참조로 들고 있는다 — 방금 추가한 행은 id 로 되찾지 못하는 경우가 있다.
  const selectedNode = selectedNodeRef.current;
  const selectedRow = selectedNode ? selectedRowSnapshot : null;

  const setSelectedValue = (key, value) => {
    if (!selectedNode) return;
    selectedNode.setDataValue(key, value);
    // 저장 시 수식의 이름을 코드로 되돌릴지는 formulaExpValue 가 있는지로 판단한다.
    if (key === "formulaExp") {
      selectedNode.setDataValue("formulaExpValue", value);
    }
    setSelectedRowSnapshot({ ...selectedNode.data });
    refreshMeasureCounts(gridApi);
  };

  const toOptions = (list, getKey, getLabel, current) => {
    const currentKey = isNil(current) ? "" : String(getKey(current));
    const options = (list ?? []).map(item => {
      const key = String(getKey(item));
      return { key, value: item, label: getLabel(item), selected: key === currentKey };
    });

    if (!isNil(current) && !options.some(option => option.selected)) {
      options.unshift({ key: currentKey, value: current, label: getLabel(current), selected: true, disabled: true });
    }

    return options;
  };

  const buildPanelFields = () => {
    if (!selectedRow) return [];

    const editable = isEditableMeasure(selectedRow);

    return [
      {
        key: "name", label: t("measure"), kind: "text",
        /* ⚠️ 입력칸이다 — 여기 번역문을 채우면 한 글자만 고쳐도 그 글자가 값으로 굳는다.
           "sum" 을 쳐 두면 다시 열 때 "SUM" 이 들어와 있고, 그대로 저장되면 이름이 바뀐다. */
        value: GridUtils.measureLabel(selectedRow.name, codeUtil.dpMeasure[selectedRow.measureCd]?.name),
        disabled: !editable,
      },
      {
        key: "valueTypeCd", label: t("type"), kind: "select", disabled: !editable,
        options: toOptions(size(measureType) < 1 ? valueType.current : measureType,
          v => v, v => GridUtils.gridValueL10N(v), selectedRow.valueTypeCd),
      },
      {
        key: "aggRuleCd", label: t("aggregation"), kind: "select", disabled: !editable,
        options: toOptions(aggRules, r => r.cd, r => GridUtils.gridValueL10N(r.name), selectedRow.aggRuleCd),
      },
      {
        key: "disaggRuleCd", label: t("disaggregation"), kind: "select", disabled: !editable,
        options: toOptions(disaggRules, r => r.cd, r => GridUtils.gridValueL10N(r.name), selectedRow.disaggRuleCd),
      },
      {
        key: "option", label: t("option"), kind: "text",
        value: optionFormatter({ data: selectedRow, value: selectedRow.option }),
        disabled: true,
      },
      {
        key: "descTxt", label: t("descTxt"), kind: "text",
        value: selectedRow.descTxt,
        disabled: !editable,
      },
      {
        key: "formulaExp", label: t("formula"), kind: "formula", wide: true,
        value: selectedRow.formulaExp,
        disabled: !selectedRow.computeFlg || !editable,
        insert: formulaInsert,
        onHelp: e => setPopoverEl(e.currentTarget),
        help: selectedRow.computeFlg && !editable ? t("MSG_BasicMeasureFormulaLocked") : undefined,
        onKeyDown: e => {
          if (e.key === "[" && selectedRow.computeFlg) {
            setMenuEl(e.currentTarget);
          }
        },
      },
    ];
  };

  /*
   * Edit Target(배분 기준 단위)은 짝 measure 까지 함께 뒤집는 규칙이 있어
   * 기존 그리드 핸들러를 그대로 태운다(params 모양만 맞춰준다).
   */
  const handlePanelToggle = (key, value) => {
    if (key === "disaggUnitFlg" && selectedNode) {
      onClikedDisaggUnit({ data: selectedRow, node: selectedNode, column: "disaggUnitFlg", value: selectedRow.disaggUnitFlg });
      setSelectedRowSnapshot({ ...selectedNode.data });
      return;
    }
    setSelectedValue(key, value);
  };

  const retrieveMeasures = () => {
    measureService.getAll({isActive: true})
      .then(response => {
        if (response.data?.results) {
          const results = response.data.results.filter(row => !CHANNEL_ONLY_MEASURE_CDS.includes(row.measureCd));
          let allActive = true;
          for (const row of results) {
            if (!row.activeFlg) {
              allActive = false;
            }
          }
          setGridDatas(results);
          setSelectAll(allActive);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(gridApi);
      });
  };

  const retrieveChannelMeasures = () => {
    measureService.getChannelMeasure({isActive: true})
      .then(response => {
        if (response.data?.results) {
          let allActive = true;
          for (const row of response.data.results) {
            if (!row.activeFlg) {
              allActive = false;
            }
          }
          setGridDatas(response.data.results);
          setSelectAll(allActive);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(gridApi);
      });
  };

  const retrievePlanVersion = () => {
    if (!selectedVersion.current?.objVal?.LR_VER_ID) {
      if (isChannel) {
        retrieveChannelMeasures();
      } else {
        retrieveMeasures();
      }
      return;
    }

    planVersionService.get(selectedVersion.current.objVal.LR_VER_ID)
      .then(response => {
        versions.current = [{...response.data}]
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        if (isChannel) {
          retrieveChannelMeasures();
        } else {
          retrieveMeasures();
        }
      });
  };

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: module.DP,
      activeFlg: true
    };

    planConfigService.getAll(param)
      .then((response) => {
        if (response?.data?.results) {
          for (const result of response?.data?.results) {
            if (isEqual(result.configCd, LR)) {
              selectedVersion.current = result;
            } else if (isEqual(result.configCd, DISAGGREGATION_TARGET)) {
              disaggTarget.current = result.strVal;
              if (result?.objVal?.target) {
                disaggTargetForSalesPlan.current = result?.objVal?.target;
              }
            } else if (isEqual(result.configCd, AVG_FLG)) {
              avgMonths.current = result.numVal;
            }
          }
        }

        if (isEmpty(selectedVersion.current)) {
          const row = {
            moduleCd: module.DP,
            activeFlg: true,
            configCd: LR,
            boolVal: false,
            objVal: {}
          }
          selectedVersion.current = row;
        }
      }).finally(() => {
      retrievePlanVersion();
    })
      .catch(e => {
        debug(e);
      });
  };

  const refreshGrid = () => {
    if (isChannel) {
      retrieveChannelMeasures();
    } else {
      retrieveMeasures();
    }
  };

  const setActiveCheck = (activeGridData, gridData, startNum) => {
    let isChange = false;
    let updatedGridData = gridData;
    let newStartNum = startNum;

    const customMeasures = activeGridData.filter(m => m.measureCd?.includes(USER_MEASURE));
    const customFormulas = customMeasures.map(m => m?.formulaExpValue);
    const customMeasureCds = customMeasures.map(m => m?.measureCd);
    const userMeasures = measureModal.filter(m => m.measureCd?.includes(USER_MEASURE));

    userMeasures.forEach(userMeasure => {
      const hasMatch = customFormulas.some(
        customFormula =>
          includes(customFormula, userMeasure.measureCd) &&
          !includes(customMeasureCds, userMeasure?.measureCd)
      );

      if (hasMatch) {
        updatedGridData = updatedGridData.map(d =>
          d.measureCd === userMeasure.measureCd
            ? { ...d, activeFlg: true, seqNum: newStartNum }
            : d
        );
        newStartNum++;
        isChange = true;
      }
    });

    return { isChange, gridData: updatedGridData };
  };

  const saveMeasure = () => {
    const gridData = [];
    let startNum = 1;
    const activeGridData = [];
    if (gridApi) {
      gridApi?.forEachNode(node => {
        if (node.data.activeFlg) {
          node.data.seqNum = startNum;
          startNum++;
          activeGridData.push(node.data);
        } else {
          node.data.seqNum = MAX_SIZE;
        }
        if (node.data.formulaExpValue) {
          node.data.formulaExp = WorkbenchUtil.changeExpression(node.data.formulaExp, oriMeasureList, false);
        }
        node.data.aggRuleCd = node.data?.aggRuleCd?.cd;
        node.data.disaggRuleCd = node.data?.disaggRuleCd?.cd;
        gridData.push({ ...node.data });
      });
    }

    const activeCheck = setActiveCheck(activeGridData, gridData, startNum);
    const updatedGridData = activeCheck?.gridData ?? gridData;

    if (updatedGridData.length > 0) {
      if (isChannel) {
        measureService.upsertChannel(updatedGridData)
          .then(response => {
            if (response) {
              refreshGrid();
            }
            const results = {};
            data.reload = true;
            if (activeCheck?.isChange) {
              setSnackInfo({ open: true, severity: validation, content: t("MSG_FormulaUserMeasureValidation") });
              setTimeout(() =>{
                modalResultData(results);
                close();
              }, 2000)
            } else {
              modalResultData(results);
              close();
            }
          })
          .catch(_e => {
            setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
          })
          .then(() => {
            refreshGrid();
          });
      } else {
        measureService.upsert(updatedGridData)
          .then(response => {
            if (response) {
              refreshGrid();
            }
            const results = {};
            data.reload = true;
            if (activeCheck?.isChange) {
              setSnackInfo({ open: true, severity: validation, content: t("MSG_FormulaUserMeasureValidation") });
              setTimeout(() =>{
                modalResultData(results);
                close();
              }, 2000)
            } else {
              modalResultData(results);
              close();
            }
          })
          .catch(_e => {
            setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
          })
          .then(() => {
            refreshGrid();
          });
      }
    } else {
      setSnackInfo({ open: true, severity: noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  /*
   * 삭제는 오른쪽 패널에서 고른 measure 하나를 지운다(체크박스 열 없이).
   * 아직 저장 전인 새 행은 서버에 없으므로 그리드에서만 걷어낸다.
   */
  const removeMeasure = () => {
    gridApi.stopEditing(false);

    const target = selectedRow;
    if (!target) {
      setSnackInfo({ open: true, severity: noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    if (isNil(target.id)) {
      gridApi.applyTransaction({ remove: [selectedNode.data] });
      pickMeasureNode(null);
      refreshMeasureCounts(gridApi);
      return;
    }

    const itemIds = [target.id];

    if (itemIds.length > 0) {
      measureService
        .remove(itemIds)
        .then(() => {
          setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          const message = e.response?.data;
          setSnackInfo({ open: true, severity: saveFail, content: t(message ?? "MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          refreshGrid();
        });
    } else {
      setSnackInfo({ open: true, severity: noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = action => {
    if (action === "DELETE") {
      removeMeasure();
    } else if (action === "CANCEL") {
      close();
    }
  };

  const onGridReady = params => {
    DataState.initialize(params.api);
    setGridApi(params.api);
  };

  const handleSave = () => {
    gridApi.stopEditing(false);
    saveMeasure();
  };

  const handleClose = () => {
    gridApi?.stopEditing(false);
    setTimeout(() => {
      const allStateDatas = DataState.getAllStateData(gridApi);
      if (allStateDatas.length > 0) {
        setDialogInfo({ open: true, title: "cancel", content: t("MSG_ChangeConfirm") });
      } else {
        close(props);
      }
    }, 100);
  };

  useEffect(() => {
    // 열고 닫을 때마다 목록을 다시 불러오므로, 이전에 고른 행(노드)은 버린다.
    pickMeasureNode(null);
    selectAllTouched.current = false;
    if (open) {
      retrievePlanConfig();
    } else {
      setSnackInfo({ open: false, severity: "", content: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (measureModal.length > 0) {
      setColumnDefs(measureColumnDefs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureModal]);

  // 헤더(28) + 행(28) + 위아래 여백(8) 기준, 280~520 사이로 맞춘다.
  const listHeight = Math.min(520, Math.max(280, measureModal.length * 28 + 36));

  return (
    <>
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("measureMgmt")}
        titleAdornment={
          <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StatPill component="span" label={t("measure")} value={measureCounts.total}/>
            <StatPill component="span" label={t("active")} value={measureCounts.active} accent/>
          </Box>
        }
        onSave={handleSave}
        /* 푸터 좌측의 표시 건수·범례 — 셸의 부가 액션 자리를 그대로 쓴다 */
        extraActions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box component="span" sx={{ fontSize: FS.BODY, color: "text.disabled" }}>
              {`${measureCounts.shown} / ${measureCounts.total}`}
            </Box>
            <Box component="span" sx={{ fontSize: FS.BODY, color: "text.disabled" }}>·</Box>
            <Box component="span" sx={{ fontSize: FS.BODY, color: "text.disabled" }}>{`ƒ = ${t("compute")}`}</Box>
          </Box>
        }
        width="min(1240px, 94vw)">
        <Box id="dp-measure-modal-top">
          {/* 툴바 — 검색/필터/추가.
              밑줄을 긋지 않는다: 본문이 넘칠 때 DialogContent 에 스크롤바가 생기면서 이 선만 오른쪽이
              15px 짧아져, 셸의 <ModalRule /> 두 줄과 좌우 여백이 어긋나 보였다.
              구분은 아래 그리드의 헤더 행(자체 배경)이 이미 해 준다. */}
          <Box
            sx={{
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
              pb: 1.5,
            }}
          >
            {/* 검색은 왼쪽, 필터·추가는 오른쪽. 상자 모양은 우측 패널 입력칸과 같은 규격으로 둔다
                (size="small" 기본 테두리·모서리 + FS.SUB) — 알약(borderRadius 999)이면 혼자 튄다. */}
            <TextField
              size="small"
              variant="outlined"
              value={measureKeyword}
              placeholder={t("search")}
              onChange={e => {
                setMeasureKeyword(e.target.value);
                applyMeasureFilter({ keyword: e.target.value });
              }}
              sx={{
                width: 220,
                "& .MuiInputBase-input": { fontSize: FS.SUB },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }}/>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <SegmentedControl
              value={activeOnly ? ACTIVE_ONLY : ALL_MEASURES}
              onChange={v => {
                const onlyActive = v === ACTIVE_ONLY;
                setActiveOnly(onlyActive);
                applyMeasureFilter({ activeOnly: onlyActive });
              }}
              options={[
                { value: ALL_MEASURES, label: t("all") },
                { value: ACTIVE_ONLY, label: t("active") },
              ]}
            />
            {/* 릴리즈된 버전에서는 추가가 막혀 있으므로, 누르고 나서 알리지 말고 버튼에서 미리 알린다. */}
            <Tooltip title={isReleasedVersion ? t("MSG_ReleaseVersionMeasureAddNotAvail") : ""}>
              <span>
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<AddIcon/>}
                  size="small"
                  disabled={isReleasedVersion}
                  onClick={addRow}
                  sx={TOOLBAR_BUTTON_SX}>
                  {t("addMeasure")}
                </Button>
              </span>
            </Tooltip>
          </Box>

          {/*
            * 본문 — 왼쪽 목록(순서 드래그) + 오른쪽 편집 패널.
            * 높이는 행 수에 맞춘다(최대 520) — 고정 vh 로 두면 measure 가 적을 때 아래가 텅 빈다.
            */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 660px) minmax(0, 1fr)" },
              height: listHeight,
            }}
          >
            <Box
              className="ag-theme-balham measure-sheet-grid"
              sx={{
                height: "100%", boxSizing: "border-box", px: 1, py: 0.5,
                borderRight: { md: 1 }, borderColor: { md: "divider" },
              }}
            >
              <AgGridReact
                ref={gridRef}
                rowData={measureModal}
                onGridReady={onGridReady}
                columnDefs={GridUtils.getColumnDefs(columnDefs)}
                {...DefaultGridSetting({ title: "measureMgmt" })}
                defaultColDef={{ sortable: false, resizable: true, editable: true, wrapHeaderText: false, autoHeaderHeight: false }}
                rowHeight={28}
                headerHeight={28}
                rowDragManaged
                animateRows
                onRowDragEnd={onRowDragEnd}
                isExternalFilterPresent={isMeasureFilterPresent}
                doesExternalFilterPass={doesMeasureFilterPass}
                onModelUpdated={params => refreshMeasureCounts(params.api)}
                onCellValueChanged={onCellValueChanged}
                onRowClicked={params => pickMeasureNode(params.node)}
                rowClassRules={{ "measure-row-picked": params => params.node?.id === selectedNodeId }}
                rowSelection={{ mode: 'multiRow',
                  enableClickSelection: false,
                  checkboxes: false,
                  headerCheckbox: false }}
                selectionColumnDef={{
                  hide: true,
                }}
                tooltipShowDelay={100}
                tooltipHideDelay={5000}
              />
            </Box>

            <MeasureDetailPanel
              selected={!!selectedRow}
              instanceKey={selectedNodeId}
              title={selectedRow ? GridUtils.measureLabel(selectedRow.name, codeUtil.dpMeasure[selectedRow.measureCd]?.name) : ""}
              placeholderTitle={t("addMeasure")}
              empty={t("MSG_SelectMeasureToEdit")}
              badges={selectedRow ? [
                ...(selectedRow.computeFlg ? [{ label: "f", kind: "fx" }] : []),
                ...(selectedRow.valueTypeCd ? [{ label: GridUtils.gridValueL10N(selectedRow.valueTypeCd), kind: "chip" }] : []),
              ] : []}
              toggles={selectedRow ? [
                { key: "activeFlg", label: t("activeFlg"), checked: selectedRow.activeFlg },
                { key: "computeFlg", label: t("compute"), checked: selectedRow.computeFlg, disabled: !isEditableMeasure(selectedRow) },
                { key: "disaggUnitFlg", label: t("editTarget"), checked: selectedRow.disaggUnitFlg },
              ] : []}
              fields={buildPanelFields()}
              onChange={handlePanelToggle}
              onDelete={selectedRow?.simFlg
                ? () => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })
                : undefined}
              deleteLabel={t("delete")}
            />
          </Box>

          <Dialog
            open={dialogInfo.open}
            onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
            actionName={dialogInfo.actionName}
            title={dialogInfo.title}
            content={dialogInfo.content}
            onHandler={handleDialog}
          />
          <Snackbar
            open={snackInfo.open}
            onClose={() => setSnackInfo({ ...snackInfo, open: false })}
            severity={snackInfo.severity}
            content={snackInfo.content}
          />
        </Box>
      </BaseConfigModal>
      <Popover
        id={id}
        open={popoverOpen}
        anchorEl={popoverEl}
        onClose={popoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}>
        <Typography sx={{ p: 1.2, fontSize: FS.H2 }}>
          {t("MSG_EditableFormulaExplanation")}
          <br></br>
          {t("availableCals")}:<strong> + - * / </strong>
          <br></br>
          {t("MSG_FormulaExplanation")} <br></br>
          ex&#41; &#91;MEASURE&#93;* 1.1
        </Typography>
      </Popover>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "outlined-basic",
        }}
        anchorEl={menuEl}
        open={menuOpen}
        onClose={menuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: 300,
          },
        }}>
        {oriMeasureList?.map(computeMeasure => {
          return (
            <MenuItem
              onClick={() => addExpression(computeMeasure.name)}
              value={computeMeasure.name}
              key={computeMeasure.name}>
              {GridUtils.gridValueL10N(computeMeasure.name)}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default withTranslation()(DPMeasureModal);
